import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, LogOut, Shield, User, Menu, PanelRight, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAssistancePanel } from '../../contexts/AssistancePanelContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * TopBar - App shell header (Glass Cockpit redesign).
 * 64px desktop / 56px mobile. Shield logo + "FLIGHT SIM ACADEMY" +
 * "FENIX A320 EDITION" amber badge on the left, actions on the right.
 * Below the desktop breakpoint (1024px) the menu button always opens the
 * dedicated mobile nav drawer (DashboardLayout), independent of the
 * desktop sidebar's open/collapsed state.
 */
const TopBar = () => {
  const { user, logout } = useAuth();
  const { toggle: toggleSidebar, toggleMobileDrawer } = useSidebar();
  const { isOpen: isAssistanceOpen, toggle: toggleAssistance } = useAssistancePanel();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  const isFounder = user?.role === 'founder';

  const handleMenuClick = () => {
    // Below the desktop breakpoint, the left nav is always a dedicated overlay
    // drawer with its own state - never the desktop inline-collapse state.
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      toggleMobileDrawer();
    } else {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate('/login');
  };

  return (
    <header
      className="h-14 md:h-16 flex-shrink-0 flex items-center justify-between px-3 md:px-7 border-b border-glass-border-nav bg-glass-bg"
      data-testid="top-bar"
    >
      {/* Left: menu toggle + logo + title + edition badge */}
      <div className="flex items-center gap-3 md:gap-4 min-w-0">
        <button
          onClick={handleMenuClick}
          className="w-10 h-10 flex items-center justify-center rounded-gc-button text-glass-text-body hover:bg-glass-card transition-colors flex-shrink-0"
          aria-label="Menu"
          data-testid="menu-toggle-btn"
        >
          <Menu className="w-5 h-5" />
        </button>

        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" data-testid="nav-logo">
          <img
            src="/fsa_shield.png"
            alt="Flight Sim Academy"
            className="w-9 h-9 flex-shrink-0 object-contain"
          />
          <span className="hidden sm:block font-barlow-condensed font-bold text-[20px] tracking-[0.14em] text-glass-text-main truncate">
            FLIGHT SIM ACADEMY
          </span>
        </Link>

        <span
          className="hidden md:inline-flex items-center font-plex-mono text-[12px] text-glass-amber border border-glass-amber-border rounded-gc-badge px-[9px] py-[5px] flex-shrink-0"
          data-testid="edition-badge"
        >
          FENIX A320 EDITION
        </span>
      </div>

      {/* Right: assistance, settings, admin, account menu */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        <button
          onClick={toggleAssistance}
          className={`hidden sm:flex items-center gap-2 h-10 px-3.5 rounded-gc-button border text-sm font-medium transition-colors ${
            isAssistanceOpen
              ? 'border-glass-cyan text-glass-cyan bg-glass-current-row'
              : 'border-glass-border-outline text-glass-text-body hover:bg-glass-card'
          }`}
          data-testid="assistance-toggle-btn"
        >
          <PanelRight className="w-4 h-4" />
          <span className="hidden lg:inline">In-Flight Assistance</span>
        </button>
        <button
          onClick={toggleAssistance}
          className="sm:hidden w-10 h-10 flex items-center justify-center rounded-gc-button text-glass-text-body hover:bg-glass-card transition-colors"
          aria-label="In-Flight Assistance"
          data-testid="assistance-toggle-btn-mobile"
        >
          <PanelRight className="w-5 h-5" />
        </button>

        <Link
          to="/settings"
          className="w-10 h-10 flex items-center justify-center rounded-gc-button text-glass-text-body hover:bg-glass-card transition-colors"
          aria-label="Settings"
          data-testid="settings-link"
        >
          <Settings className="w-5 h-5" />
        </Link>

        {isFounder && (
          <Link
            to="/admin"
            className="w-10 h-10 flex items-center justify-center rounded-gc-button text-glass-amber hover:bg-glass-card transition-colors"
            aria-label="Admin"
            data-testid="admin-link"
          >
            <Shield className="w-5 h-5" />
          </Link>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1.5 h-10 pl-2 pr-2.5 rounded-gc-button border border-glass-border-outline text-glass-text-body hover:bg-glass-card transition-colors"
              data-testid="account-menu-trigger"
            >
              <span className="w-6 h-6 rounded-full bg-glass-current-row flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-glass-cyan" />
              </span>
              <ChevronDown className="w-3.5 h-3.5 hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-glass-card border-glass-border-card text-glass-text-main">
            <DropdownMenuLabel className="text-glass-text-muted font-plex-mono text-xs truncate">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-glass-border-card" />
            <DropdownMenuItem asChild>
              <Link to="/account" data-testid="account-menu-account-link" className="cursor-pointer">
                Account
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} disabled={loggingOut} data-testid="account-menu-logout-btn" className="cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" />
              {loggingOut ? 'Logging out...' : 'Logout'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default TopBar;
